import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import { red, green, grey } from '@mui/material/colors';

import ViewInArIcon from "@mui/icons-material/ViewInAr";
import GavelIcon from "@mui/icons-material/Gavel";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {isEmpty, isEqual, isUndefined, replace} from "lodash";

import mrpOrderListService from "@plannel/services/mp/mrp-order-list-service"
import planVersionService from "@plannel/services/data/plan-version-service";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import VersionAutocomplete from "@plannel/components/filter/VersionAutocomplete";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import DateCondition from "@plannel/components/DateCondition";
import {isNull} from "lodash/lang";
import NumberUtil from "@plannel/utils/number-util";

const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, module } = CodeUtil;
const { RELEASED, PLANNED } = versionStatusCode;
const { MP } = module;
const { orderListCondition } = CodeUtil;
const EXCEL_EXPORT = "excelExport";

const MrpOrderList = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const [versionList, setVersionList] = useState([]);
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.version ?? null);

  const itemSearch = useRef(reduxViewState?.itemSearch ? reduxViewState.itemSearch : "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [sortParams, setSortParams] = useState({});

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [mrpOrderList, setMrpOrderList] = useState([]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const versionTimeBuckets = selectVersion?.planConfigObj?.TIME_BUCKETS;
  const isMonthBucket = !isEmpty(versionTimeBuckets) && isEqual(versionTimeBuckets[0].UNIT, CodeUtil.buckets.MONTH);

  const initPeriod = { start: reduxViewState?.period?.start ? new ZDate(reduxViewState.period.start) : null, end: reduxViewState?.period?.end ? new ZDate(reduxViewState.period.end) : null };

  const [period, setPeriod] = useState(initPeriod);
  const [selectedSearch, setSelectedSearch] = useState(reduxViewState?.selectedSearch ?? null);
  const userData = JSON.parse(localStorage.getItem("user"));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currencyRenderer = (param, isExport = false) => {
    let symbol = userData?.currencySymbol;
    const currencyCd = param?.data?.currencyCd
    if (currencyCd) {
      symbol = CodeUtil.getCurrencySymbol(currencyCd);
    }
    const value = param?.data?.[param?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    if (isExport) {
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const columnDefs = [
    { headerName: "locCd", field: "locCd", width: 120, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "locName", field: "locName", width: 120, type: ["nonEditableColumn"] },
    { headerName: "supplierCd", field: "supplierCd", width: 120, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "supplierName", field: "supplierName", width: 120, type: ["nonEditableColumn"] },
    { headerName: "orderDate", field: "displayOrderDate", width: 120, type: ["nonEditableColumn"] },
    { headerName: "dueIn", field: "displayDueIn", width: 120, type: ["nonEditableColumn"] },
    { headerName: "itemCd", field: "itemCd", width: 120, type: ["nonEditableColumn"], cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName", width: 120, type: ["nonEditableColumn"] },
    { headerName: "purchaseOrderQty", field: "purchaseOrderQty", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
    { headerName: "purchaseOrderAmt", field: "purchaseOrderAmt", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer },
    { headerName: "purchaseUnitPrice", field: "purchaseUnitPrice", width: 130, type: ["numberColumn", "nonEditableColumn", "rightAligned"] }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (selectVersion) {
      params.versionId = selectVersion.id;
      const timeBuckets = selectVersion?.planConfigObj?.TIME_BUCKETS;
      params.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

     if (selectedSearch) {
      params.condition = selectedSearch.cd;
    }

    if (period?.start) {
      params.startDate = period?.start;
    }

    if (period?.end) {
      params.endDate = period?.end;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return { ...params, ...sortParams };
  }

  const retrieveOrderList = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mrpOrderListService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setMrpOrderList(results);
        isEmpty(results) ? setGridPages(0) : setGridPages(totalPages);
      })
      .catch(e => {
        const { message } = e.response?.data;
        if (message) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t(message) });
        }
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, {  version: selectVersion, itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, filter: filterValue, currentPage, pageSize, selectedSearch, period });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const retrieveVersion = receivedVersion => {
    const params = { moduleCd: MP, statusCds: [PLANNED, RELEASED], orderByColumn: "updatedTs", sortType: "desc" };

    planVersionService.getAll(params)
    .then(response => {
      const { results } = response.data;
      if (results) {
        setVersionList(results);

        if (receivedVersion) {
          const currentVersion = results.find(result => isEqual(result.id, receivedVersion.id));
          currentVersion ? setSelectVersion(currentVersion) : setSelectVersion(results[0]);
        } else {
          if (reduxViewState?.version) {
            const reduxVersion = results.find(result => isEqual(result.id, reduxViewState.version.id));
            if (isUndefined(reduxVersion)) {
              setSelectVersion(results[0]);
            }
          } else {
            setSelectVersion(results[0]);
          }
        }
      }
    })
    .catch(e => {
      debug(e);
    });
  };

  const onGridReady = (params) => {
    retrieveVersion();
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveOrderList();
    }
  }

  useEffect(() => {
    if (gridLoading && selectVersion) {
      retrieveOrderList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && selectVersion) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectVersion, filterValue, period, selectedSearch]);

  useEffect(() => {
    if (gridLoading) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading]);

  const setToolTip = (version) => {
    if (version?.planConfigObj) {
      const { PLAN_START_DATE, PLAN_END_DATE } = version.planConfigObj;

      return `${PLAN_START_DATE} ~ ${PLAN_END_DATE}`
    }

    return null;
  }

  const iconColor = planVersion => {
    if (planVersion) {
      const { statusCd } = planVersion;
      if (isEqual(statusCd, RELEASED)) {
        return grey[900];
      } else if (isEqual(statusCd, PLANNED)) {
        return green[600];
      }

      return red[600];
    }

    return null;
  }

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <VersionAutocomplete
          options={versionList}
          value={selectVersion}
          onChange={(_, newValue) => setSelectVersion(newValue)}
          label={t("version")}
          getIcon={(option) => (isEqual(option.statusCd, RELEASED) ? GavelIcon : ViewInArIcon)}
          getIconColor={iconColor}
          getTooltip={setToolTip}
          minWidth={250}
          sx={{ mr: 0, pr: 1 }}
          inputIconSx={{ ml: 1 }}
        />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <DateCondition
          conditionList={orderListCondition}
          period={period}
          useAutoComplete
          views={isMonthBucket ? ['year', 'month'] : ['year', 'month', 'day']}
          dateFormat={isMonthBucket? 'yyyy-MM' : 'yyyy-MM-dd'}
          openTo={isMonthBucket ? 'month' : 'day'}
          selectedSearch={selectedSearch}
          setSelectedSearch={setSelectedSearch}
          setPeriod={setPeriod}
        />
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={mrpOrderListService}  columnDefs={columnDefs} params={getRequestParams(false)} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <AgGridReact
          ref={gridRef}
          rowData={mrpOrderList}
          statusBar={statusBar}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
        />
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(MrpOrderList);
